import React, { useId, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  Award,
  BadgeCheck,
  Clock,
  DollarSign,
  ShieldCheck,
  ThumbsUp,
} from 'lucide-react';
// Imported here rather than relied on from ProviderLayout: this row is
// rendered on Profile card faces too (ListingFront, ExternalListingFront),
// which are mounted outside the provider layout, so the pv-* pill tokens
// would otherwise be unstyled on half its insertion points.
import '../../styles/provider-design-system.css';

/**
 * The provider-facing trust badges on a listing, a card face or a
 * conversation screen.
 *
 * D1.11.1: EVERY STRING COMES FROM THE SERVER. There is no client copy map,
 * and adding one would be the defect: the company trophy case and this row
 * read the same award, and the moment the tooltip is written twice the
 * provider-facing sentence can start describing something Rx Rooster never
 * checked. Only the icon and the colour are chosen here, and neither of them
 * makes a claim.
 *
 * D1.11.5: click or focus opens the tooltip, hover also opens it on a desktop
 * pointer. Hover alone would put the sentence that says WHAT was verified out
 * of reach of every provider on a phone, which is most of them.
 *
 * @param {Array<{key, level, label, tooltip, earnedAt?}>} badges  server rows;
 *   an empty or missing array renders nothing at all, not an empty container
 * @param {'sm'|'md'} [size]
 * @param {string} [className]
 */

// Colour per badge key, from contract 7.3. These are the provider design
// system's own pill variants, so the row cannot drift from the rest of the
// provider surface. An unrecognised key falls back to slate rather than
// disappearing: an award the server chose to send is one the provider is
// meant to see.
const VARIANT_BY_KEY = {
  verified_employer: 'teal',
  fast_responder: 'amber',
  compensation_confirmed: 'sky',
  provider_recommended: 'violet',
  preferred_partner: 'emerald',
};

const ICON_BY_KEY = {
  verified_employer: ShieldCheck,
  fast_responder: Clock,
  compensation_confirmed: DollarSign,
  provider_recommended: ThumbsUp,
  preferred_partner: Award,
};

/**
 * D1.11.2: listing cards and listing detail render ORGANIZATION- and
 * REQUISITION-level badges only. A recruiter-level award (Fast Responder,
 * Provider Recommended) names a person, and a card is not a conversation:
 * those render only on the conversation screens where a recruiter is
 * actually in context.
 */
export const listingLevelBadges = (badges) =>
  Array.isArray(badges)
    ? badges.filter((b) => b && b.level !== 'recruiter')
    : [];

export default function TrustBadgeRow({
  badges,
  size = 'sm',
  className = '',
}) {
  // One open tooltip at a time. Held here rather than left to each
  // OverlayTrigger's own state so `aria-describedby` can be set on the pill
  // only while its tooltip is actually on screen: a describedby pointing at
  // an element that is not rendered is a dangling reference to a screen
  // reader, which is worse than none.
  const [openIndex, setOpenIndex] = useState(null);
  const rowId = useId();

  const rows = Array.isArray(badges) ? badges.filter(Boolean) : [];
  if (rows.length === 0) return null;

  return (
    <div
      className={`pv-trust-badges ${className}`.trim()}
      data-testid="trust-badge-row"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: size === 'md' ? 8 : 6,
        alignItems: 'center',
      }}
    >
      {rows.map((badge, index) => {
        const Icon = ICON_BY_KEY[badge.key] ?? BadgeCheck;
        const variant = VARIANT_BY_KEY[badge.key] ?? 'slate';
        const tipId = `${rowId}-trust-${index}`;
        const open = openIndex === index;

        return (
          <OverlayTrigger
            key={`${badge.key}-${badge.level ?? index}`}
            placement="top"
            trigger={['hover', 'focus', 'click']}
            rootClose
            show={open}
            onToggle={(next) => setOpenIndex(next ? index : null)}
            overlay={<Tooltip id={tipId}>{badge.tooltip}</Tooltip>}
          >
            <button
              type="button"
              data-testid="trust-badge"
              data-badge-key={badge.key}
              data-badge-level={badge.level}
              className={`pv-badge pv-badge--${variant}`}
              aria-describedby={open ? tipId : undefined}
              // The pill is a disclosure, not a navigation: it opens its own
              // tooltip and nothing else. Stopping the click keeps it from
              // also tapping through to the card it sits on.
              onClick={(e) => e.stopPropagation()}
              style={{
                border: 'none',
                cursor: 'pointer',
                fontSize: size === 'md' ? 12 : 11,
                fontFamily: 'inherit',
                padding: size === 'md' ? '3px 10px' : '2px 8px',
              }}
            >
              <Icon size={size === 'md' ? 13 : 12} aria-hidden="true" />
              {badge.label}
            </button>
          </OverlayTrigger>
        );
      })}
    </div>
  );
}
